/* Cart reward meter: animated progress, one-click quick picks and the free wrapping kit claim.
   Shopify decides whether the kit is actually free; a paid claim is rolled back. */
(() => {
  const STORE_KEY = 'pradem-rewards';
  let pending = false;
  const root = () => window.Shopify.routes.root;
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const conversionRate = (currency) => {
    if (currency === 'PLN') return 1;
    const current = window.Shopify.currency;
    const rate = Number(current?.rate);
    return current?.active === currency && Number.isFinite(rate) && rate > 0 ? rate : null;
  };
  const convertedThreshold = (base, rate) => Math.ceil(base * rate - 1e-7);
  const money = (cents, currency) => new Intl.NumberFormat(document.documentElement.lang || 'en', {
    style: 'currency', currency,
  }).format(cents / 100);
  const qualifyingTotal = (state, widget) => state.items.reduce((sum, item) => {
    const qualifies = (widget.dataset.qualifyingProducts || '').includes(`|${item.product_id}|`);
    return sum + (qualifies && item.product_id !== Number(widget.dataset.giftProduct) &&
      item.original_line_price === item.final_line_price ? item.original_line_price : 0);
  }, 0);

  const refreshMeter = (widget) => {
    if (!widget.dataset.giftBase) return;
    const currency = widget.dataset.currency;
    const rate = conversionRate(currency);
    if (!rate) return; // Keep an unverified foreign-currency claim hidden.
    const gift = convertedThreshold(Number(widget.dataset.giftBase), rate);
    const shipping = convertedThreshold(Number(widget.dataset.shippingBase), rate);
    const giftTotal = Number(widget.dataset.giftTotal);
    const total = Number(widget.dataset.total);
    const giftUnlocked = giftTotal >= gift;
    const shippingUnlocked = total >= shipping;
    const claimed = widget.dataset.giftClaimed === 'true';
    const progress = Math.min(100, total / shipping * 100);
    widget.dataset.giftThreshold = gift;
    widget.dataset.shippingThreshold = shipping;
    widget.dataset.progress = progress;
    widget.dataset.unlocked = Number(giftUnlocked) + Number(shippingUnlocked);
    const headline = widget.querySelector('.pradem-rewards__headline');
    if (!headline) { widget.removeAttribute('data-converting'); return; }
    const template = !giftUnlocked ? widget.dataset.headlineGift : !shippingUnlocked ? widget.dataset.headlineShipping : widget.dataset.headlineDone;
    headline.innerHTML = template.replace('__amount__', money(!giftUnlocked ? gift - giftTotal : shipping - total, currency));
    widget.querySelector('.pradem-rewards__count').textContent = widget.dataset.countText.replace('__unlocked__', widget.dataset.unlocked);
    const track = widget.querySelector('[role="progressbar"]');
    track?.setAttribute('aria-valuenow', Math.round(progress));
    widget.querySelector('.pradem-rewards__fill')?.style.setProperty('--progress', `${progress}%`);
    const marker = widget.querySelector('.pradem-rewards__marker');
    marker?.style.setProperty('--at', `${gift / shipping * 100}%`);
    marker?.classList.toggle('is-unlocked', giftUnlocked);
    for (const [goal, unlocked, threshold] of [['gift', giftUnlocked, gift], ['shipping', shippingUnlocked, shipping]]) {
      const row = widget.querySelector(`[data-reward-goal="${goal}"]`);
      if (!row) continue;
      row.classList.toggle('is-unlocked', unlocked);
      row.querySelector('.pradem-rewards__goal-state').textContent = goal === 'gift' && claimed ? widget.dataset.claimedText :
        unlocked ? widget.dataset.unlockedText : widget.dataset.thresholdText.replace('__amount__', money(threshold, currency));
    }
    const claimButton = widget.querySelector('[data-pradem-claim]');
    if (claimButton) claimButton.hidden = !giftUnlocked || claimed;
    const remaining = !giftUnlocked ? gift - giftTotal : shipping - total;
    widget.classList.toggle('is-close', remaining > 0 && remaining <= (giftUnlocked ? shipping : gift) / 4);
    // Quick picks remain suggestions; a stale pre-conversion label must not promise to close the gap.
    widget.querySelectorAll('.pradem-rewards__pick-tag').forEach((tag) => tag.hidden = true);
    widget.removeAttribute('data-converting');
  };

  const cart = async () => {
    const response = await fetch(`${root()}cart.js`);
    if (!response.ok) throw new Error('cart');
    return response.json();
  };

  const sections = () => {
    const result = [];
    for (const [id, selector] of [
      ['main-cart-items', '.js-contents'],
      ['main-cart-footer', '.cart__blocks'],
      ['cart-icon-bubble', '.shopify-section'],
      ['CartDrawer', '.drawer__inner'],
    ]) {
      const element = document.getElementById(id);
      if (element) result.push({ id, selector, section: element.dataset.id || (id === 'CartDrawer' ? 'cart-drawer' : id) });
    }
    return result;
  };

  const mutate = async (endpoint, data, descriptors) => {
    const response = await fetch(`${root()}cart/${endpoint}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...data, sections: descriptors.map((item) => item.section), sections_url: location.pathname }),
    });
    const result = await response.json();
    if (!response.ok || result.status) throw new Error('cart mutation');
    return result;
  };

  const render = (result, descriptors) => {
    descriptors.forEach(({ id, section, selector }) => {
      const markup = result.sections?.[section];
      if (!markup) return;
      const html = new DOMParser().parseFromString(markup, 'text/html');
      const target = document.getElementById(id);
      const source = html.querySelector(selector);
      if (target && source) (target.querySelector(selector) || target).innerHTML = source.innerHTML;
    });
    if (typeof result.item_count === 'number') {
      const empty = result.item_count === 0;
      document.getElementById('main-cart-footer')?.classList.toggle('is-empty', empty);
      document.querySelector('cart-items')?.classList.toggle('is-empty', empty);
      document.querySelector('cart-drawer')?.classList.toggle('is-empty', empty);
    }
  };

  const announce = (message) =>
    document.querySelectorAll('.pradem-rewards__status').forEach((element) => {
      element.textContent = message;
    });
  const notify = (data) => publish(PUB_SUB_EVENTS.cartUpdate, { source: 'pradem-rewards', cartData: data });

  // The bar fills from where it stood on the previous render; a newly reached goal flashes.
  const animate = (widget) => {
    const fill = widget.querySelector('.pradem-rewards__fill');
    if (!fill) return;
    const progress = Number(widget.dataset.progress) || 0;
    const unlocked = Number(widget.dataset.unlocked) || 0;
    let previous = null;
    try {
      previous = JSON.parse(sessionStorage.getItem(STORE_KEY));
      sessionStorage.setItem(STORE_KEY, JSON.stringify({ progress, unlocked }));
    } catch {
      /* Storage unavailable: render the final state without animation. */
    }
    if (reducedMotion()) return;
    const from = previous ? previous.progress : 0;
    if (from !== progress) {
      fill.style.transition = 'none';
      fill.style.setProperty('--progress', `${from}%`);
      fill.getBoundingClientRect();
      fill.style.transition = '';
      fill.style.setProperty('--progress', `${progress}%`);
    }
    if (previous && unlocked > previous.unlocked) {
      widget.querySelectorAll('.pradem-rewards__goal.is-unlocked').forEach((goal, index) => {
        if (index >= previous.unlocked) goal.classList.add('is-celebrating');
      });
    }
  };

  const addPick = async (button) => {
    if (pending) return;
    pending = true;
    button.disabled = true;
    const descriptors = sections();
    try {
      const result = await mutate('add', { items: [{ id: Number(button.dataset.prademAdd), quantity: 1 }] }, descriptors);
      const state = await cart();
      render({ ...state, sections: result.sections }, descriptors);
      announce(document.querySelector('pradem-cart-rewards')?.dataset.added || '');
      notify(state);
    } catch {
      announce(button.closest('pradem-cart-rewards')?.dataset.addError || '');
      if (button.isConnected) button.disabled = false;
    } finally {
      pending = false;
    }
  };

  const claim = async (widget) => {
    if (pending) return;
    pending = true;
    const button = widget.querySelector('[data-pradem-claim]');
    button.disabled = true;
    widget.setAttribute('aria-busy', 'true');
    const descriptors = sections();
    let claimKey;
    try {
      const before = await cart();
      const product = Number(widget.dataset.giftProduct);
      const threshold = Number(widget.dataset.giftThreshold);
      if (!threshold || qualifyingTotal(before, widget) < threshold) throw new Error('threshold');
      if (before.items.some((item) => item.product_id === product && (item.final_line_price === 0 || item.properties?._pradem_reward === 'wrapping-kit')))
        throw new Error('already claimed');
      let result = await mutate(
        'add',
        { items: [{ id: Number(widget.dataset.giftVariant), quantity: 1, properties: { _pradem_reward: 'wrapping-kit' } }] },
        descriptors
      );
      claimKey = result.items?.find((item) => item.properties?._pradem_reward === 'wrapping-kit')?.key;
      const after = await cart();
      const gift = after.items.find((item) => item.product_id === product && item.properties?._pradem_reward === 'wrapping-kit');
      if (!gift) throw new Error('missing gift');
      claimKey = gift.key;
      if (gift.final_line_price !== 0) {
        result = await mutate('change', { id: gift.key, quantity: 0 }, descriptors);
        render(result, descriptors);
        announce(widget.dataset.notFree);
        notify(result);
        return;
      }
      render({ ...after, sections: result.sections }, descriptors);
      claimKey = undefined;
      announce(widget.dataset.success);
      notify(after);
    } catch {
      if (claimKey) {
        try {
          const rollback = await mutate('change', { id: claimKey, quantity: 0 }, descriptors);
          render(rollback, descriptors);
          notify(rollback);
        } catch {
          /* The next cart validation rechecks any remaining claimed line. */
        }
      }
      announce(widget.dataset.error);
      if (button.isConnected) button.disabled = false;
    } finally {
      pending = false;
      widget.removeAttribute('aria-busy');
    }
  };

  // A free claim must not become a paid line after reducing the order.
  const validate = async () => {
    if (pending || !document.querySelector('pradem-cart-rewards')) return;
    pending = true;
    try {
      let state = await cart();
      const claimed = state.items.filter((item) => item.properties?._pradem_reward === 'wrapping-kit');
      const descriptors = sections();
      let changed = false;
      for (const item of claimed) {
        if (item.quantity > 1 || item.final_line_price !== 0) {
          state = await mutate('change', { id: item.key, quantity: item.quantity > 1 ? 1 : 0 }, descriptors);
          const remaining = state.items.find((line) => line.properties?._pradem_reward === 'wrapping-kit');
          if (remaining && remaining.final_line_price !== 0) state = await mutate('change', { id: remaining.key, quantity: 0 }, descriptors);
          changed = true;
        }
      }
      if (changed) {
        render(state, descriptors);
        notify(state);
      }
    } catch {
      /* Retry with the next native cart update. */
    } finally {
      pending = false;
    }
  };

  if (!customElements.get('pradem-cart-rewards')) {
    customElements.define(
      'pradem-cart-rewards',
      class extends HTMLElement {
        connectedCallback() {
          this.addEventListener('click', (event) => {
            const pick = event.target.closest('[data-pradem-add]');
            if (pick) addPick(pick);
            else if (event.target.closest('[data-pradem-claim]')) claim(this);
          });
          refreshMeter(this);
          animate(this);
          if (!pending) validate();
        }
      }
    );
  }

  if (!customElements.get('pradem-shipping-message')) {
    customElements.define('pradem-shipping-message', class extends HTMLElement {
      connectedCallback() {
        const rate = conversionRate(this.dataset.currency);
        if (rate) this.textContent = this.dataset.template.replace('__amount__',
          money(convertedThreshold(Number(this.dataset.base), rate), this.dataset.currency));
      }
    });
  }

  subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
    if (event.source !== 'pradem-rewards') validate();
  });
})();
